import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useLanguage } from '@/contexts';
import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    function: user?.role || '',
    planType: 'Starter'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string; } | null>(null);
  
  // NEW: Widget variant state
  const [widgetVariant, setWidgetVariant] = useState<'badge' | 'footer' | 'floating' | 'icon'>(() => {
    const stored = localStorage.getItem('usage_widget_variant');
    return (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
  });
  const [savedWidgetVariant, setSavedWidgetVariant] = useState<'badge' | 'footer' | 'floating' | 'icon'>(() => {
    const stored = localStorage.getItem('usage_widget_variant');
    return (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
  });

  const location = useLocation();

  // Update form data when user data changes
  React.useEffect(() => {
    if (user) {
      const nameParts = (user.name || '').split(' ');
      setFormData({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user.email || '',
        function: user.role || '',
        planType: 'Starter'
      });
    }
  }, [user]);

  // Handle email update success from verification page
  React.useEffect(() => {
    if (location.state?.emailUpdateSuccess) {
      setMessage({
        type: 'success',
        text: `${t('emailUpdateSuccess')} ${location.state.newEmail}!`
      });
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, t]);

  // Handle password change success
  React.useEffect(() => {
    if (location.state?.passwordChangeSuccess) {
      setMessage({
        type: 'success',
        text: location.state.message || t('passwordChangedSuccessfully') || 'Password changed successfully!'
      });
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, t]);

  const handleCancel = () => {
    const nameParts = (user?.name || '').split(' ');
    setFormData({
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      email: user?.email || '',
      function: user?.role || '',
      planType: 'Starter'
    });
    setMessage(null);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await authenticatedFetch(API_URLS.USER_PROFILE.UPDATE, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          role: formData.function
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update user context with new data
        setUser(data.user);
        setMessage({ type: 'success', text: t('profileUpdatedSuccessfully') });
      } else {
        // Handle both old and new error response formats
        const errorMessage = data.error?.message || data.error || data.message || t('failedUpdateProfile');
        setMessage({ type: 'error', text: errorMessage });
      }
    } catch (error) {
      console.error('Profile update error:', error instanceof Error ? error.message : t('unknownError'));
      setMessage({ type: 'error', text: t('anErrorOccurredWhileUpdatingYourProfile') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = () => {
    navigate('/update-email');
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // NEW: Widget variant change handler (only updates local state)
  const handleWidgetVariantChange = (variant: 'badge' | 'footer' | 'floating' | 'icon') => {
    setWidgetVariant(variant);
  };

  // NEW: Save widget preferences
  const handleSaveWidgetPreferences = () => {
    localStorage.setItem('usage_widget_variant', widgetVariant);
    setSavedWidgetVariant(widgetVariant);
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('widgetVariantChanged'));
    
    setMessage({ 
      type: 'success', 
      text: t('widgetPreferenceUpdated') || 'Widget preference updated successfully!' 
    });
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => setMessage(null), 3000);
  };

  // NEW: Cancel widget preferences changes
  const handleCancelWidgetPreferences = () => {
    setWidgetVariant(savedWidgetVariant);
    setMessage(null);
  };

  const FUNCTION_OPTIONS = [
    { value: '', labelKey: 'selectFunction' },
    { value: 'Developer', labelKey: 'developer' },
    { value: 'Designer', labelKey: 'designer' },
    { value: 'Product Manager', labelKey: 'productManager' },
    { value: 'Data Scientist', labelKey: 'dataScientist' },
    { value: 'Marketing', labelKey: 'marketing' },
    { value: 'Other', labelKey: 'other' }
  ];

  // NEW: Widget variant options
  const WIDGET_VARIANT_OPTIONS = [
    { 
      value: 'badge', 
      labelKey: 'widgetBadgeSidebar', 
      descriptionKey: 'widgetBadgeDescription'
    },
    { 
      value: 'footer', 
      labelKey: 'widgetFooterBar', 
      descriptionKey: 'widgetFooterDescription'
    },
    { 
      value: 'floating', 
      labelKey: 'widgetFloating', 
      descriptionKey: 'widgetFloatingDescription'
    },
    { 
      value: 'icon', 
      labelKey: 'widgetIconOnly', 
      descriptionKey: 'widgetIconDescription'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-4 px-3 sm:px-4 md:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
        {/* Account Settings Card */}
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-300">
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
            <div className="mb-3 sm:mb-4">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-slate-100 transition-colors duration-200">{t('accountSettings')}</h1>
            </div>

            {/* Success/Error Message */}
            {message && (
              <div className={`mb-3 p-2 rounded-md transition-colors duration-200 ${message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
                }`}>
                {message.text}
              </div>
            )}

            <div className="space-y-3">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 text-left transition-colors duration-200">
                  {t('fullName')}:
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2">
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-md text-sm text-left transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                    placeholder={t("firstName")}
                  />
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-md text-sm text-left transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                    placeholder={t("lastName")}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 text-left transition-colors duration-200">
                  {t('email')}:
                </label>
                <div className="px-2 py-1.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-md text-sm text-gray-700 dark:text-slate-300 text-left transition-colors duration-200">
                  {user?.email || t('notAvailable')}
                </div>
              </div>

              {/* Function */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 text-left transition-colors duration-200">
                  {t('function')}:
                </label>
                <select
                  value={formData.function}
                  onChange={(e) => handleInputChange('function', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 rounded-md text-sm text-left transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                >
                  {FUNCTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email Verified */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1 text-left transition-colors duration-200">
                  {t('emailVerified')}:
                </label>
                <div className="px-2 py-1.5 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-md text-sm text-green-600 dark:text-green-400 font-medium text-left transition-colors duration-200">
                  {t('yes')}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 sm:space-x-3 mt-4">
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="w-full sm:w-auto px-4 py-2 bg-brand-dark-blue dark:bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue dark:hover:bg-blue-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50"
              >
                {isLoading ? t('saving') : t('saveChanges')}
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-200 text-sm font-medium rounded-md hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-300">
          <div className="px-4 py-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4 transition-colors duration-200">{t("changePassword") || "Change Password"}</h2>
            <div className="space-y-3">
              <p className="text-gray-600 dark:text-slate-300 transition-colors duration-200">
                {t("passwordNeedChange") || "Need to update your password? Change it here."}
              </p>
              <button
                onClick={() => navigate('/change-password')}
                className="px-4 py-2 bg-brand-dark-blue dark:bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue dark:hover:bg-blue-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
              >
                {t("changePassword") || "Change Password"}
              </button>
            </div>
          </div>
        </div>

        {/* Update Email Address Card */}
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-300">
          <div className="px-4 py-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4 transition-colors duration-200">{t("updateEmailAddress")}</h2>
            {user?.isSSOOnly || user?.hasSSO ? (
              <div className="space-y-3">
                <div className="bg-brand-light-gray dark:bg-blue-900/30 border border-gray-200 dark:border-blue-700 rounded-md p-3 transition-colors duration-300">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-brand-electric-blue dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-brand-dark-blue dark:text-blue-300 transition-colors duration-200">
                        {t('ssoAccountEmailRestriction')}
                      </h3>
                      <div className="mt-2 text-sm text-brand-dark-blue dark:text-blue-300 transition-colors duration-200">
                        <p>
                          {t('yourAccountIsLinkedTo')} {user.ssoProviders?.join(', ') || 'SSO'} {t('authentication')}.
                          {t('emailUpdatesManagedThroughYourSSOProvider')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  disabled
                  className="px-4 py-2 bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-sm font-medium rounded-md cursor-not-allowed transition-colors duration-200"
                >
                  {t("updateEmailAddress")} ({t('ssoDisabled')})
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-gray-600 dark:text-slate-300 transition-colors duration-200">
                  {t('emailNeedChange')}
                </p>
                <button
                  onClick={handleUpdateEmail}
                  className="px-4 py-2 bg-brand-dark-blue dark:bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue dark:hover:bg-blue-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
                >
                  {t('updateEmailAddress')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* NEW: Usage Widget Preferences Card */}
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 transition-colors duration-300">
          <div className="px-4 py-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-4 transition-colors duration-200">
              {t("usageWidgetPreferences") || "Usage Widget Preferences"}
            </h2>

            {/* Success/Error Message for Widget Preferences */}
            {message && (
              <div className={`mb-3 p-3 rounded-md transition-colors duration-200 ${message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300'
                }`}>
                <div className="flex items-center">
                  {message.type === 'success' ? (
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span className="text-sm font-medium">{message.text}</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-gray-600 dark:text-slate-300 text-sm transition-colors duration-200">
                {t("selectHowYouWantToSeeYourUsageInformation") || "Select how you want to see your usage information across the app."}
              </p>
              
              {/* Widget Variant Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-left transition-colors duration-200">
                  {t("widgetType") || "Widget Type"}:
                </label>
                <div className="space-y-2">
                  {WIDGET_VARIANT_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => handleWidgetVariantChange(option.value as 'badge' | 'footer' | 'floating' | 'icon')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        widgetVariant === option.value
                          ? 'border-brand-electric-blue dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-slate-700 hover:border-brand-electric-blue hover:bg-gray-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <div className="flex items-start">
                        <div className={`mt-0.5 h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
                          widgetVariant === option.value
                            ? 'border-brand-electric-blue dark:border-blue-400'
                            : 'border-gray-300 dark:border-slate-600'
                        }`}>
                          {widgetVariant === option.value && (
                            <div className="h-3 w-3 rounded-full bg-brand-electric-blue dark:bg-blue-400"></div>
                          )}
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-slate-100 transition-colors duration-200">
                            {t(option.labelKey)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 transition-colors duration-200">
                            {t(option.descriptionKey)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={handleSaveWidgetPreferences}
                disabled={widgetVariant === savedWidgetVariant}
                className="px-4 py-2 bg-brand-dark-blue dark:bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue dark:hover:bg-blue-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('saveChanges')}
              </button>
              <button
                onClick={handleCancelWidgetPreferences}
                disabled={widgetVariant === savedWidgetVariant}
                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-200 text-sm font-medium rounded-md hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;